import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  SafeAreaView,
} from "react-native";
import { postFeeLookup } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ProductType } from "../../../../../view-models/productType";
import type { FeeInfo } from "../../../../../view-models/feeInfo";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import { useGlobalError }  from '../../../../uiComponents/GlobalErrorProvider';
// import { Ionicons } from "@expo/vector-icons"; // or react-native-vector-icons

// Server-driven options will be shown here

type HowTheyReceiveStep = {
  onCountrySelected?: () => void;
};


export default function HowTheyReceiveStep({ onCountrySelected }: HowTheyReceiveStep) {
  const { t } = useTranslation();
  const { showError } = useGlobalError();
  const [selected, setSelected] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [feeInfos, setFeeInfos] = useState<FeeInfo[]>([]);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const selectedReceiveCurrency = useSessionStore((s: SessionState) => (s as any).selectedReceiveCurrency);
  const setSelectedSendOption = useSessionStore((s: SessionState) => (s as any).setSelectedSendOption);
  const setPendingSendOption = useSessionStore((s: SessionState) => (s as any).setPendingSendOption);
  const lastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).lastFeeLookupResponse);

  const getOptionKey = (fee: FeeInfo): string => {
    return JSON.stringify({
      sendCurrency: fee?.SendAmounts?.SendCurrency || null,
      exchangeRate: FeeLookupHelper.getExchangeRate(fee) || null,
      receiveCurrency: FeeLookupHelper.getReceiveCurrency(fee) || null,
      deliveryOptDisplayName: fee.DeliveryOptDisplayName || null,
      receiveAgentId: (fee as any).ReceiveAgentId || null,
    });
  };

  const formatExchangeRate = (fee: FeeInfo): string => {
    const sendCurrency = fee?.SendAmounts?.SendCurrency || 'USD';
    const rate = FeeLookupHelper.getExchangeRate(fee);
    const receiveCurrency = FeeLookupHelper.getReceiveCurrency(fee);
    const rateNum = Number(rate);
    const rateFixed = isFinite(rateNum) ? rateNum.toFixed(4) : String(rate);
    return `1 ${sendCurrency} = ${rateFixed} ${receiveCurrency}`;
  };

  const formatFee = (fee: FeeInfo): string => {
    const amount = Number(fee?.SendAmounts?.TotalSendFees || 0);
    const currency = fee?.SendAmounts?.SendCurrency || 'USD';
    return `Fee: ${amount.toFixed(2)} ${currency}`;
  };

  // Utility function to group by a "composite key"
function groupBy<T, K>(array: T[], keyGetter: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  array.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

  useEffect(() => {
    const useOrFetch = async () => {
      if (!agentId || !unitProfileId || !selectedDestinationCountryCode) return;
      try {
        setIsLoading(true);
        setError(null);
        let result: any = lastFeeLookupResponse;
        if (!result) {
          const payload: any = {
            agentID: agentId,
            unitProfileID: unitProfileId,
            amount: 1,
            sendCurrency: 'USD',
            destinationCountry: selectedDestinationCountryCode,
            productType: ProductType.SEND,
            allOptions: true,
          };
          if ((selectedDestinationCountryCode === 'USA' || selectedDestinationCountryCode === 'CAN') && selectedDestinationStateAbbr) {
            payload.destinationState = selectedDestinationStateAbbr;
          }
          result = await postFeeLookup(payload);
        }

        console.log('HowTheyReceiveStep FeeLookup result (reuse if available):', result);
        // Unwrap nested data if needed and derive array
        let inner: any = result;
        if (typeof result?.data === 'string') {
          try { inner = JSON.parse(result.data); } catch { inner = result; }
        } else if (typeof result?.data === 'object') {
          inner = result.data;
        }
        const arr: FeeInfo[] = Array.isArray(inner?.FeeInfos) ? inner.FeeInfos : [];

        // Build sendOptions from parsed array
        const sendOptions: FeeInfo[] = (() => {
          // Prefer first non-USD receive currency from the response
          const nonUsdCurrency = (() => {
            const first = arr.find((f: FeeInfo) => {
              const rc = (FeeLookupHelper.getReceiveCurrency(f) || '').toUpperCase();
              return rc && rc !== 'USD';
            });
            return first ? (FeeLookupHelper.getReceiveCurrency(first) || '').toUpperCase() : '';
          })();

          const filtered = arr.filter(
            (fee: FeeInfo) =>
              fee.ErrorInfo == null &&
              fee.SendAmounts != null &&
              (!nonUsdCurrency || (FeeLookupHelper.getReceiveCurrency(fee) || '').toUpperCase() === nonUsdCurrency)
          );

          const grouped = groupBy(filtered, (f: FeeInfo) =>
            JSON.stringify({
              sendCurrency: f.SendAmounts!.SendCurrency,
              exchangeRate: FeeLookupHelper.getExchangeRate(f),
              receiveCurrency: FeeLookupHelper.getReceiveCurrency(f),
              deliveryOptDisplayName: f.DeliveryOptDisplayName,
              totalSendFees: f.SendAmounts!.TotalSendFees,
              receiveAgentName: f.ReceiveAgentName,
            })
          );

          return Array.from(grouped.values()).map((group) => group[0]);
        })();

        console.log('HowTheyReceiveStep sendOptions:', sendOptions);
        setFeeInfos(sendOptions);
      } catch (e: any) {
        console.error('FeeLookup failed in HowTheyReceiveStep:', e);
        setError('Failed to load delivery options');
      } finally {
        setIsLoading(false);
      }
    };
    useOrFetch();
  }, [agentId, unitProfileId, selectedDestinationCountryCode, selectedDestinationStateAbbr, lastFeeLookupResponse]);


  return (
    <SafeAreaView style={styles.safe}>
      <View style={styles.container}>
        {/* Title */}
        <Text style={styles.title}>{t("RECEIVE_METHOD_TITLE")}</Text>
        {isLoading && (
          <>
            <Text style={{ marginBottom: 8 }}>{t("RECEIVE_METHOD_LOADING")}</Text>
          </>
        )}

        {error && (
          <>
            <Text style={{ marginBottom: 8, color: 'red' }}>{error}</Text>
            <Text style={{ marginBottom: 8, color: 'red' }}>{t("RECEIVE_METHOD_ERROR")}</Text>
          </>
        )}
        {/* List */}
        <FlatList
          data={feeInfos}
          keyExtractor={(item) => getOptionKey(item)}
          contentContainerStyle={{ paddingBottom: 100 }}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.methodRow}
              onPress={() => {
                const key = getOptionKey(item);
                setSelected(key);
                // Stage selection; will be committed on global Continue click
                console.log('item===>', item);
                onCountrySelected?.();
                setPendingSendOption(item);
              }}
            >
              <View style={{ flex: 1 }}>
                <Text style={styles.methodTitle}>{item.DeliveryOptDisplayName || item.ReceiveAgentName || t("RECEIVE_METHOD_DELIVERY_OPTION")}</Text>
                <Text style={styles.methodDetails}>{formatExchangeRate(item)}</Text>
                <Text style={styles.methodDetails}>{formatFee(item)}</Text>
                {!!(item.ReceiveAgentAbbreviation) && (
                  <Text style={styles.methodDetails}>{item.ReceiveAgentAbbreviation}</Text>
                )}
              </View>
              <View
                style={[
                  styles.radioOuter,
                  selected === getOptionKey(item) && styles.radioOuterSelected,
                ]}
              >
                {selected === getOptionKey(item) && <View style={styles.radioInner} />}
              </View>
            </TouchableOpacity>
          )}
          ListEmptyComponent={!isLoading && !error ? (
            <Text style={{ color: '#6B7280' }}>{t("RECEIVE_METHOD_NO_OPTIONS")}</Text>
          ) : null}
        />
      </View>

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    paddingHorizontal: 24,
    marginTop: 16,
    backgroundColor: "#fff"
  },
  container: { paddingHorizontal: 24 },
  title: { fontSize: 22, fontWeight: "700", marginBottom: 4, color: "#111" },
  subtitle: { fontSize: 14, color: "#6B7280", marginBottom: 16 },
  methodRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  methodTitle: { fontSize: 16, fontWeight: "600", color: "#111" },
  methodDetails: { fontSize: 13, color: "#6B7280", marginTop: 2 },
  providersRow: { flexDirection: "row", marginTop: 6 },
  providerCircle: { fontSize: 20, marginRight: 4 },
  moreCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#6B7280",
    alignItems: "center",
    justifyContent: "center",
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
    marginTop: 4,
  },
  radioOuterSelected: { borderColor: "#000" },
  radioInner: { width: 10, height: 10, borderRadius: 5, backgroundColor: "#000" },
  footer: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: 16,
    backgroundColor: "#fff",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  continueBtn: {
    backgroundColor: "#111",
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  continueText: { color: "#fff", fontSize: 16, fontWeight: "600" },
});